import { useEffect, useRef, useState } from "react";

/**
 * Gravador de áudio local (web/PWA) via MediaRecorder. Produz um Blob de áudio que
 * o pai salva em `experiencia.midias` (reusa todo o pipeline: salvarFoto → tocar →
 * indicador 🔊 → backup). Nada sai do aparelho.
 *
 * Segurança sensorial & privacidade: só grava por toque, mostra a duração, tem
 * limite (60s) e permite refazer/descartar antes de salvar. A voz da criança é
 * dado sensível — fica local.
 *
 * No WebView nativo (iOS) o MediaRecorder é instável; lá o caminho robusto é um
 * plugin Capacitor (fase 2). Aqui degradamos com uma mensagem clara.
 */
const MAX_SEG = 60;

function escolherMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  return cands.find((t) => MediaRecorder.isTypeSupported(t));
}

function mmss(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

type Estado = "ocioso" | "gravando" | "gravado" | "erro";

export function GravadorAudio({
  onSalvar,
  salvando = false,
}: {
  onSalvar: (blob: Blob) => void | Promise<void>;
  salvando?: boolean;
}) {
  const [estado, setEstado] = useState<Estado>("ocioso");
  const [segundos, setSegundos] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const timerRef = useRef<number | null>(null);
  const segRef = useRef(0);
  const urlRef = useRef<string | null>(null);

  function setUrlSeguro(u: string | null) {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = u;
    setUrl(u);
  }
  function pararTracks() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }
  function limparTimer() {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  // limpeza no desmonte (sem deps: roda só uma vez)
  useEffect(
    () => () => {
      limparTimer();
      pararTracks();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  function pararGravacao() {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  }

  async function iniciar() {
    setErro(null);
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setErro("este aparelho não permite gravar áudio aqui. anexe um arquivo.");
      setEstado("erro");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = escolherMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        blobRef.current = blob;
        setUrlSeguro(URL.createObjectURL(blob));
        pararTracks();
        limparTimer();
        setEstado("gravado");
      };
      rec.start();
      recRef.current = rec;
      segRef.current = 0;
      setSegundos(0);
      setEstado("gravando");
      timerRef.current = window.setInterval(() => {
        segRef.current += 1;
        setSegundos(segRef.current);
        if (segRef.current >= MAX_SEG) pararGravacao();
      }, 1000);
    } catch {
      setErro("não foi possível acessar o microfone (permissão negada?).");
      setEstado("erro");
      pararTracks();
    }
  }

  function refazer() {
    blobRef.current = null;
    setUrlSeguro(null);
    segRef.current = 0;
    setSegundos(0);
    setEstado("ocioso");
  }

  async function salvar() {
    if (!blobRef.current) return;
    await onSalvar(blobRef.current);
    refazer();
  }

  return (
    <div className="card gravador">
      {estado === "ocioso" && (
        <button className="btn btn-bloco btn-grande" onClick={iniciar}>
          🎙️ gravar áudio
        </button>
      )}

      {estado === "gravando" && (
        <>
          <div className="grav-status" aria-live="polite">
            <span className="grav-dot" aria-hidden />
            gravando… <span className="grav-timer">{mmss(segundos)}</span>
            <span className="meta" style={{ color: "var(--text-suave)" }}>
              {" "}
              / {mmss(MAX_SEG)}
            </span>
          </div>
          <button
            className="btn btn-primario btn-bloco btn-grande"
            style={{ marginTop: "var(--s3)" }}
            onClick={pararGravacao}
          >
            ■ parar
          </button>
        </>
      )}

      {estado === "gravado" && (
        <>
          <div className="grav-status" style={{ color: "var(--accent-forte)" }}>
            ✓ gravado · {mmss(segundos)}
          </div>
          {url && (
            <audio src={url} controls style={{ width: "100%", marginTop: "var(--s3)" }} />
          )}
          <div className="row" style={{ marginTop: "var(--s3)" }}>
            <button className="btn crescer" onClick={refazer} disabled={salvando}>
              ↺ refazer
            </button>
            <button
              className="btn btn-primario crescer"
              onClick={salvar}
              disabled={salvando}
            >
              {salvando ? "salvando…" : "✓ usar este áudio"}
            </button>
          </div>
        </>
      )}

      {estado === "erro" && (
        <>
          <p className="meta" style={{ color: "var(--st-teste-fg)", margin: 0 }}>
            {erro}
          </p>
          <button
            className="btn btn-bloco"
            style={{ marginTop: "var(--s3)" }}
            onClick={iniciar}
          >
            tentar de novo
          </button>
        </>
      )}
    </div>
  );
}
